/**
 * Dashboard Component Actualizado
 * Fecha: 23 Enero 2026
 * Muestra estadísticas actualizadas de la red Terranet
 */

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // En produccion usa URLs relativas (nginx hace proxy), en desarrollo usa la IP directa
  const API_URL = '';

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/stats`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      setError('Error al conectar con el servidor. Verifica que el backend esté activo.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Cargando estadísticas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorIcon}>⚠️</div>
        <h2 style={styles.errorTitle}>Error de Conexión</h2>
        <p style={styles.errorText}>{error}</p>
        <button onClick={fetchStats} style={styles.retryButton}>
          Reintentar
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={styles.errorContainer}>
        <p>No hay datos disponibles</p>
      </div>
    );
  }

  const { totales, advertencias, top_10_zonas } = stats;

  return (
    <div style={styles.dashboardContainer}>
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Dashboard - Red Terranet</h1>
        <p style={styles.subtitle}>Sistema de Gestión de Fibra Óptica - Guayaquil, Ecuador</p>
        <button onClick={fetchStats} style={styles.refreshButton} disabled={loading}>
          {loading ? '⏳ Actualizando...' : '🔄 Actualizar'}
        </button>
      </div>

      <div style={styles.statsGrid}>
        <StatCard
          title="Zonas"
          value={totales.zonas}
          icon="🗺️"
          color="#ff6b6b"
          description="Zonas de cobertura"
        />
        <StatCard
          title="Nodos"
          value={totales.nodos}
          icon="📡"
          color="#4ecdc4"
          description="Nodos de red principales"
        />
        <StatCard
          title="Cajas NAP"
          value={totales.cajas_nap}
          icon="📦"
          color="#f7b731"
          description={`${advertencias.cajas_sin_zona} sin zona asignada`}
          warning={advertencias.cajas_sin_zona > 0}
        />
        <StatCard
          title="Fibra Óptica"
          value={totales.fibra_recableado}
          icon="🔗"
          color="#5f27cd"
          description={`${advertencias.fibras_sin_zona} sin zona asignada`}
          warning={advertencias.fibras_sin_zona > 0}
        />
      </div>

      {(advertencias.cajas_sin_zona > 0 || advertencias.fibras_sin_zona > 0) && (
        <div style={styles.warningBox}>
          <div style={styles.warningIcon}>⚠️</div>
          <div style={styles.warningContent}>
            <h3 style={styles.warningTitle}>Elementos sin Zona Asignada</h3>
            <ul style={styles.warningList}>
              {advertencias.cajas_sin_zona > 0 && (
                <li>{advertencias.cajas_sin_zona} cajas NAP sin zona (usar ST_Within para asignar)</li>
              )}
              {advertencias.fibras_sin_zona > 0 && (
                <li>{advertencias.fibras_sin_zona} líneas de fibra sin zona (usar ST_Intersects para asignar)</li>
              )}
            </ul>
          </div>
        </div>
      )}

      <div style={styles.chartContainer}>
        <h2 style={styles.chartTitle}>Top 10 Zonas por Cantidad de Cajas NAP</h2>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={top_10_zonas}
            margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="zona"
              angle={-45}
              textAnchor="end"
              height={150}
              tick={{ fill: '#333', fontSize: 12 }}
            />
            <YAxis
              tick={{ fill: '#333', fontSize: 12 }}
              label={{ value: 'Cantidad de Cajas', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '10px'
              }}
            />
            <Legend />
            <Bar
              dataKey="cajas"
              fill="#f7b731"
              name="Cajas NAP"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={styles.infoBox}>
        <h3 style={styles.infoTitle}>ℹ️ Información del Sistema</h3>
        <div style={styles.infoGrid}>
          <InfoItem label="Base de Datos" value="10.100.253.66:5432" />
          <InfoItem label="GeoServer" value="10.100.253.70:8080" />
          <InfoItem label="Backend API" value="Proxy Nginx /api" />
          <InfoItem label="Última Actualización" value={new Date().toLocaleString('es-EC')} />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, description, warning }) => (
  <div style={{...styles.statCard, borderTop: `4px solid ${color}`}}>
    <div style={styles.statIcon}>{icon}</div>
    <div style={styles.statContent}>
      <h3 style={styles.statTitle}>{title}</h3>
      <p style={{...styles.statValue, color: color}}>{value}</p>
      <p style={{
        ...styles.statDescription,
        color: warning ? '#e74c3c' : '#7f8c8d'
      }}>
        {description}
      </p>
    </div>
  </div>
);

const InfoItem = ({ label, value }) => (
  <div style={styles.infoItem}>
    <span style={styles.infoLabel}>{label}:</span>
    <span style={styles.infoValue}>{value}</span>
  </div>
);

const styles = {
  dashboardContainer: {
    padding: '20px',
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '30px',
    textAlign: 'center',
    position: 'relative',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#7f8c8d',
    marginBottom: '20px',
  },
  refreshButton: {
    padding: '10px 20px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
    transition: 'background-color 0.3s',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
  },
  statIcon: {
    fontSize: '3rem',
  },
  statContent: {
    flex: 1,
  },
  statTitle: {
    fontSize: '0.9rem',
    color: '#7f8c8d',
    marginBottom: '5px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  statDescription: {
    fontSize: '0.85rem',
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '30px',
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-start',
  },
  warningIcon: {
    fontSize: '2rem',
  },
  warningContent: {
    flex: 1,
  },
  warningTitle: {
    color: '#856404',
    marginBottom: '10px',
    fontSize: '1.2rem',
  },
  warningList: {
    color: '#856404',
    marginLeft: '20px',
    lineHeight: '1.8',
  },
  chartContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '30px',
  },
  chartTitle: {
    fontSize: '1.5rem',
    color: '#2c3e50',
    marginBottom: '20px',
    textAlign: 'center',
  },
  infoBox: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  infoTitle: {
    fontSize: '1.2rem',
    color: '#2c3e50',
    marginBottom: '15px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  infoLabel: {
    fontSize: '0.85rem',
    color: '#7f8c8d',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  infoValue: {
    fontSize: '1rem',
    color: '#2c3e50',
    fontWeight: '600',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f5f7fa',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #e0e0e0',
    borderTop: '5px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '20px',
    fontSize: '1.2rem',
    color: '#7f8c8d',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f5f7fa',
    padding: '20px',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '4rem',
    marginBottom: '20px',
  },
  errorTitle: {
    fontSize: '2rem',
    color: '#e74c3c',
    marginBottom: '10px',
  },
  errorText: {
    fontSize: '1.1rem',
    color: '#7f8c8d',
    maxWidth: '600px',
    marginBottom: '20px',
  },
  retryButton: {
    padding: '12px 30px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
};

export default Dashboard;